import { DateRange } from 'bold-ui'
import { CidadaoSelectFieldModel, EquipeSelectModel } from 'components/form'
import { LotacaoEditableTableModel } from 'components/form/editable-table/LotacaoEditableTable'
import { ProcedimentoSelectModel } from 'components/form/field/select/ProcedimentoSelectField'
import { LotacaoResponsavelFormModel } from 'components/lotacao-responsavel/model-lotacaoResponsavel'
import {
  AtividadeColetivaTableQuery,
  AtividadeColetivaVisualizarReuniaoQuery,
  AtividadeColetivaVisualizarSaudeQuery,
  FatoAtividadeColetivaReuniaoQuery,
  FatoAtividadeColetivaSaudeQuery,
  OrigemDadoEnum,
  PageParams,
  PraticaSaudeEnum,
  Profissional,
  PublicoAlvoEnum,
  SexoEnum,
  TemaReuniaoEnum,
  TemaSaudeEnum,
  TipoAtividadeEnum,
  TurnoEnum,
} from 'graphql/types.generated'

import { LocalAtividadeFieldModel } from './componentes/LocalAtividadeField'
import { ObservacaoCidadaoEditableTableModel } from './componentes/observacao-cidadao/ObservacaoCidadaoEditableTable'
import { ParticipanteEditableTableModel } from './componentes/participantes/ParticipanteEditableTable'

export type FatoAtividadeColetivaReuniaoModel = FatoAtividadeColetivaReuniaoQuery['fatoAtividadeColetiva']
export type FatoAtividadeColetivaSaudeModel = FatoAtividadeColetivaSaudeQuery['fatoAtividadeColetiva']
export type AtividadeColetivaTableItem = AtividadeColetivaTableQuery['atividadesColetivas']['content'][0] & {
  origem?: OrigemDadoEnum
}
export type AtividadeColetivaSaudeModel = AtividadeColetivaVisualizarSaudeQuery['atividadeColetiva'] & {
  origem?: OrigemDadoEnum
}
export type AtividadeColetivaReuniaoModel = AtividadeColetivaVisualizarReuniaoQuery['atividadeColetiva'] & {
  origem?: OrigemDadoEnum
  atividadeEducacaoPse?: boolean
  participantes?: ParticipanteModel[]
  numeroAvaliacoesAlteradas?: number
}

export const LOTACAO_ESTAGIO_SEM_PERMISSAO =
  'Lotações de estágio não possuem permissão para atuar como responsáveis de uma atividade coletiva.'

export const IS_TIPO_ATIVIDADE_REUNIAO: Record<TipoAtividadeEnum, boolean> = {
  REUNIAO_DE_EQUIPE: true,
  REUNIAO_COM_OUTRAS_EQUIPES_DE_SAUDE: true,
  REUNIAO_INTERSETORIAL_CONSELHO_LOCAL_DE_SAUDE_CONTROLE_SOCIAL: true,
  EDUCACAO_EM_SAUDE: false,
  ATENDIMENTO_EM_GRUPO: false,
  AVALIACAO_PROCEDIMENTO_COLETIVO: false,
  MOBILIZACAO_SOCIAL: false,
}

export const TIPOS_ATIVIDADE_REUNIAO = Object.values(TipoAtividadeEnum).filter(
  (tipo) => IS_TIPO_ATIVIDADE_REUNIAO[tipo]
)

export const TIPOS_ATIVIDADE_EM_SAUDE = Object.values(TipoAtividadeEnum).filter(
  (tipo) => !IS_TIPO_ATIVIDADE_REUNIAO[tipo]
)

export const TIPOS_ATIVIDADE_EM_SAUDE_EDUCACAO_PSE: TipoAtividadeEnum[] = TIPOS_ATIVIDADE_EM_SAUDE.filter(
  (tipo) => tipo !== TipoAtividadeEnum.ATENDIMENTO_EM_GRUPO
)

export const PRATICAS_SAUDE_EDUCACAO_PSE = [
  PraticaSaudeEnum.ANTROPOMETRIA,
  PraticaSaudeEnum.DESENVOLVIMENTO_DA_LINGUAGEM,
  PraticaSaudeEnum.PRATICAS_CORPORAIS_ATIVIDADE_FISICA,
  PraticaSaudeEnum.SAUDE_AUDITIVA,
  PraticaSaudeEnum.SAUDE_OCULAR,
  PraticaSaudeEnum.OUTRAS,
]

export const PRATICAS_SAUDE_PNCT = [
  PraticaSaudeEnum.PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_1,
  PraticaSaudeEnum.PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_2,
  PraticaSaudeEnum.PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_3,
  PraticaSaudeEnum.PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_4,
]

export const PRATICAS_SAUDE_ATENDIMENTO_EM_GRUPO: PraticaSaudeEnum[] = Object.values(PraticaSaudeEnum).filter(
  (pratica) =>
    pratica !== PraticaSaudeEnum.APLICACAO_TOPICA_DE_FLUOR &&
    pratica !== PraticaSaudeEnum.ESCOVACAO_DENTAL_SUPERVISIONADA
)

export const tipoAtividadeRecord: Record<TipoAtividadeEnum, string> = {
  REUNIAO_DE_EQUIPE: 'Reunião de equipe',
  REUNIAO_COM_OUTRAS_EQUIPES_DE_SAUDE: 'Reunião com outras equipes de saúde',
  REUNIAO_INTERSETORIAL_CONSELHO_LOCAL_DE_SAUDE_CONTROLE_SOCIAL:
    'Reunião intersetorial / Conselho local de saúde / Controle social',
  EDUCACAO_EM_SAUDE: 'Educação em saúde',
  ATENDIMENTO_EM_GRUPO: 'Atendimento em grupo',
  AVALIACAO_PROCEDIMENTO_COLETIVO: 'Avaliação / Procedimento coletivo',
  MOBILIZACAO_SOCIAL: 'Mobilização social',
}

export const tipoAtividadeByIdRecord: Record<number, TipoAtividadeEnum> = {
  1: TipoAtividadeEnum.REUNIAO_DE_EQUIPE,
  2: TipoAtividadeEnum.REUNIAO_COM_OUTRAS_EQUIPES_DE_SAUDE,
  3: TipoAtividadeEnum.REUNIAO_INTERSETORIAL_CONSELHO_LOCAL_DE_SAUDE_CONTROLE_SOCIAL,
  4: TipoAtividadeEnum.EDUCACAO_EM_SAUDE,
  5: TipoAtividadeEnum.ATENDIMENTO_EM_GRUPO,
  6: TipoAtividadeEnum.AVALIACAO_PROCEDIMENTO_COLETIVO,
  7: TipoAtividadeEnum.MOBILIZACAO_SOCIAL,
}

export const temaReuniaoRecord: Record<TemaReuniaoEnum, string> = {
  QUESTOES_ADMINISTRATIVAS_FUNCIONAMENTO: 'Questões administrativas / Funcionamento',
  PROCESSOS_DE_TRABALHO: 'Processos de trabalho',
  DIAGNOSTICO_DO_TERRITORIO_MONITORAMENTO_DO_TERRITORIO: 'Diagnóstico do território / Monitoramento do território',
  PLANEJAMENTO_MONITORAMENTO_DAS_ACOES_DA_EQUIPE: 'Planejamento / Monitoramento das ações da equipe',
  DISCUSSAO_DE_CASO_PROJETO_TERAPEUTICO_SINGULAR: 'Discussão de caso / Projeto terapêutico singular',
  EDUCACAO_PERMANENTE: 'Educação permanente',
  OUTROS: 'Outros',
}

export const temaSaudeRecord: Record<TemaSaudeEnum, string> = {
  ACOES_COMBATE_AEDES_AEGYPTI: 'Ações de combate ao Aedes aegypti',
  AGRAVOS_E_DOENCAS_NEGLIGENCIADAS: 'Agravos e doenças negligenciadas',
  ALIMENTACAO_SAUDAVEL: 'Alimentação saudável',
  AUTOCUIDADO_DE_PESSOAS_COM_DOENCAS_CRONICAS: 'Autocuidado de pessoas com doenças crônicas',
  CIDADANIA_E_DIREITOS_HUMANOS: 'Cidadania e direitos humanos',
  PREVENCAO_AO_USO_DE_ALCOOL_TABACO_E_OUTRAS_DROGAS: 'Prevenção ao uso de álcool, tabaco e outras drogas',
  ENVELHECIMENTO_CLIMATERIO_ANDROPAUSA_ETC: 'Envelhecimento (climatério, andropausa, etc)',
  PLANTAS_MEDICINAIS_FITOTERAPIA: 'Plantas medicinais / Fitoterapia',
  PREVENCAO_DA_VIOLENCIA_E_PROMOCAO_DA_CULTURA_DA_PAZ: 'Prevenção da violência e promoção da cultura da paz',
  SAUDE_AMBIENTAL: 'Saúde ambiental',
  SAUDE_BUCAL: 'Saúde bucal',
  SAUDE_DO_TRABALHADOR: 'Saúde do trabalhador',
  SAUDE_MENTAL: 'Saúde mental',
  SAUDE_SEXUAL_E_REPRODUTIVA: 'Saúde sexual e reprodutiva',
  SEMANA_SAUDE_NA_ESCOLA: 'Semana saúde na escola',
  AMAMENTACAO: 'Amamentação',
  ALIMENTACAO_COMPLEMENTAR_SAUDAVEL: 'Alimentação complementar saudável',
  OUTROS: 'Outros',
}

export const praticaSaudeRecord: Record<PraticaSaudeEnum, string> = {
  ANTROPOMETRIA: 'Antropometria',
  APLICACAO_TOPICA_DE_FLUOR: 'Aplicação tópica de flúor',
  DESENVOLVIMENTO_DA_LINGUAGEM: 'Desenvolvimento da linguagem',
  ESCOVACAO_DENTAL_SUPERVISIONADA: 'Escovação dental supervisionada',
  PRATICAS_CORPORAIS_ATIVIDADE_FISICA: 'Práticas corporais e atividade física',
  PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_1: 'PNCT sessão 1',
  PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_2: 'PNCT sessão 2',
  PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_3: 'PNCT sessão 3',
  PROGRAMA_NACIONAL_CONTROLE_TABAGISMO_4: 'PNCT sessão 4',
  SAUDE_AUDITIVA: 'Saúde auditiva',
  SAUDE_OCULAR: 'Saúde ocular',
  VERIFICACAO_DA_SITUACAO_VACINAL: 'Verificação da situação vacinal',
  OUTRAS: 'Outras',
  OUTRO_PROCEDIMENTO_COLETIVO: 'Outro procedimento coletivo',
}

export const publicoAlvoRecord: Record<PublicoAlvoEnum, string> = {
  COMUNIDADE_EM_GERAL: 'Comunidade em geral',
  CRIANCA_0_A_3_ANOS: 'Criança 0 a 3 anos',
  CRIANCA_4_A_5_ANOS: 'Criança 4 a 5 anos',
  CRIANCA_6_A_11_ANOS: 'Criança 6 a 11 anos',
  ADOLESCENTE: 'Adolescente',
  MULHER: 'Mulher',
  GESTANTE: 'Gestante',
  HOMEM: 'Homem',
  FAMILIARES: 'Familiares',
  PESSOA_IDOSA: 'Pessoa idosa',
  PESSOAS_COM_DOENCAS_CRONICAS: 'Pessoas com doenças crônicas',
  USUARIO_DE_TABACO: 'Usuário de tabaco',
  USUARIO_DE_ALCOOL: 'Usuário de álcool',
  USUARIO_DE_OUTRAS_DROGAS: 'Usuário de outras drogas',
  PESSOAS_COM_SOFRIMENTO_TRANSTORNO_MENTAL: 'Pessoas com sofrimento ou transtorno mental',
  PROFISSIONAL_DE_EDUCACAO: 'Profissional de educação',
  OUTROS: 'Outros',
}

export const atividadeColetivaTipoAtividadeRecord: Record<AtividadeColetivaTipoAtividadeEnum, string> = {
  REUNIAO: 'Reunião',
  ATIVIDADE_EM_SAUDE: 'Atividade em saúde',
}

export type ProfissionalCpfCnsIdModel = Pick<Profissional, 'id' | 'cpf' | 'cns'>

export interface CidadaoParticipanteModel {
  nome?: string
  cpf?: string
  cns?: string
  dataNascimento: LocalDate
  sexo: SexoEnum
}

export interface ParticipanteModel {
  cidadao?: Pick<CidadaoSelectFieldModel, 'id' | 'nome' | 'nomeSocial' | 'cpf' | 'cns' | 'dataNascimento' | 'sexo'>
  cidadaoParticipante?: CidadaoParticipanteModel
  peso?: number
  altura?: number
  avaliacaoAlterada?: boolean
  pnctAbandonouGrupo?: boolean
  pnctCessouHabitoFumar?: boolean
}

export interface ObservacaoCidadaoModel {
  cidadao: Pick<CidadaoSelectFieldModel, 'id' | 'nome' | 'nomeSocial' | 'cpf' | 'cns' | 'sexo' | 'dataNascimento'>
  observacao: string
}

export interface AtividadePseModel {
  saude: boolean
  educacao: boolean
}

export interface AtividadeColetivaFormModel {
  id?: ID
  lotacaoResponsavel: LotacaoResponsavelFormModel
  dataAtividade: LocalDate
  turno: TurnoEnum
  numeroParticipantes: number
  localAtividade?: LocalAtividadeFieldModel
  tipoAtividade: TipoAtividadeEnum
  temasReuniao?: TemaReuniaoEnum[]
  anotacoes?: string
  participantes?: ParticipanteEditableTableModel[]
  lotacoesEnvolvidas: LotacaoEditableTableModel[]
  atividadePse?: AtividadePseModel
  temasSaude?: TemaSaudeEnum[]
  publicoAlvo?: PublicoAlvoEnum[]
  praticasSaude?: PraticaSaudeEnum[]
  procedimento?: ProcedimentoSelectModel
  observacoes?: ObservacaoCidadaoEditableTableModel[]
  registroEnviado?: boolean
}

export enum AtividadeColetivaFonteEnum {
  PEC = 'PEC',
  OUTROS = 'OUTROS',
}

export enum AtividadeColetivaTipoAtividadeEnum {
  REUNIAO = 'REUNIAO',
  ATIVIDADE_EM_SAUDE = 'ATIVIDADE_EM_SAUDE',
}

export interface AtividadeColetivaFilterPopperFormModel {
  equipes?: EquipeSelectModel[]
  origens?: OrigemDadoEnum[]
  isProcessado?: boolean
  isReuniao?: boolean
  periodo?: DateRange
  tiposAtividade?: TipoAtividadeEnum[]
}

export type AtividadeColetivaFilterFormModel = AtividadeColetivaFilterPopperFormModel & {
  isSomenteRegistrosProprios?: boolean
  fonteRegistros?: AtividadeColetivaFonteEnum
  nomeCnsCpfProfissionalResponsavel?: string
  pageParams?: PageParams
}

export enum ParticipanteDataTableColumnEnum {
  NOME = 'nome',
  AVALIACAO_ALTERADA = 'avaliacaoAlterada',
  PESO = 'peso',
  ALTURA = 'altura',
  IMC = 'imc',
  PNCT_CESSOU_FUMAR = 'pnctCessouFumar',
  PNCT_ABANDONOU_GRUPO = 'pnctAbandonouGrupo',
}

export const defaultVisibleColumns: ParticipanteDataTableColumnEnum[] = Object.values(ParticipanteDataTableColumnEnum)
